import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import type {
  Contact,
  ContactWithDetails,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { TokenInput } from "@/components/filters/TokenInput";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { RejectionNotice } from "@/modules/contacts/screens/RejectionNotice";

/** 08AABCR1234M1Z5 — the same shape `routes/contacts.ts`' `GSTIN_PATTERN` checks. */
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9]Z[A-Z0-9]$/;

interface PhoneDraft {
  draftId: string;
  label: string;
  number: string;
  isPrimary: boolean;
}
interface EmailDraft {
  draftId: string;
  label: string;
  email: string;
  isPrimary: boolean;
}
interface AddressDraft {
  draftId: string;
  label: string;
  line1: string;
  line2: string;
  city: string;
  province: string;
  zip: string;
  country: string;
}

interface FormState {
  displayName: string;
  companyName: string;
  description: string;
  gstin: string;
  typeIds: string[];
  tags: string[];
  phones: PhoneDraft[];
  emails: EmailDraft[];
  addresses: AddressDraft[];
}

/**
 * One form for both "New contact" and "Edit details" (#387, bullet 5) — a
 * discriminated union rather than an optional `contact` prop, so a screen
 * opening it in edit mode is required to have one, and TypeScript rather
 * than a runtime check is what stops a create call from being built with a
 * `contact` nobody passed.
 */
type ContactFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful create or update, with the row as saved. */
  onSaved?: (contact: Contact) => void;
} & (
  | {
      mode: "create";
      /** Pre-assigned on a type section — Vendors' "New contact" starts
       * with Vendor already checked rather than making someone re-pick the
       * type the button they clicked was already standing on. */
      initialTypeIds?: string[];
    }
  | { mode: "edit"; contact: ContactWithDetails }
);

function blankPhone(draftId: string): PhoneDraft {
  return { draftId, label: "", number: "", isPrimary: false };
}
function blankEmail(draftId: string): EmailDraft {
  return { draftId, label: "", email: "", isPrimary: false };
}
function blankAddress(draftId: string): AddressDraft {
  return {
    draftId,
    label: "",
    line1: "",
    line2: "",
    city: "",
    province: "",
    zip: "",
    country: "",
  };
}

function initialState(props: ContactFormProps): FormState {
  if (props.mode === "edit") {
    const { contact } = props;
    return {
      displayName: contact.displayName,
      companyName: contact.companyName ?? "",
      description: contact.description ?? "",
      gstin: contact.gstin ?? "",
      typeIds: contact.typeIds,
      tags: contact.tags,
      phones: contact.phones.map((phone) => ({
        draftId: phone.id,
        label: phone.label ?? "",
        number: phone.number,
        isPrimary: phone.isPrimary,
      })),
      emails: contact.emails.map((email) => ({
        draftId: email.id,
        label: email.label ?? "",
        email: email.email,
        isPrimary: email.isPrimary,
      })),
      addresses: contact.addresses.map((address) => ({
        draftId: address.id,
        label: address.label ?? "",
        line1: address.address.line1 ?? "",
        line2: address.address.line2 ?? "",
        city: address.address.city ?? "",
        province: address.address.province ?? "",
        zip: address.address.zip ?? "",
        country: address.address.country ?? "",
      })),
    };
  }

  return {
    displayName: "",
    companyName: "",
    description: "",
    gstin: "",
    typeIds: props.initialTypeIds ?? [],
    tags: [],
    phones: [],
    emails: [],
    addresses: [],
  };
}

/** What a save sends the repository — child sets always whole, per contact.ts. */
function buildPayload(state: FormState) {
  return {
    displayName: state.displayName.trim(),
    companyName:
      state.companyName.trim() === "" ? null : state.companyName.trim(),
    description:
      state.description.trim() === "" ? null : state.description.trim(),
    gstin: state.gstin.trim() === "" ? null : state.gstin.trim().toUpperCase(),
    typeIds: state.typeIds,
    tags: state.tags,
    phones: state.phones.map((phone) => ({
      label: phone.label.trim() === "" ? null : phone.label.trim(),
      number: phone.number.trim(),
      isPrimary: phone.isPrimary,
    })),
    emails: state.emails.map((email) => ({
      label: email.label.trim() === "" ? null : email.label.trim(),
      email: email.email.trim(),
      isPrimary: email.isPrimary,
    })),
    // No primary flag collected here — the ticket's own field list for
    // addresses stops at country, unlike phones and emails.
    addresses: state.addresses.map((address) => ({
      label: address.label.trim() === "" ? null : address.label.trim(),
      isPrimary: false,
      address: {
        line1: address.line1.trim() || undefined,
        line2: address.line2.trim() || undefined,
        city: address.city.trim() || undefined,
        province: address.province.trim() || undefined,
        zip: address.zip.trim() || undefined,
        country: address.country.trim() || undefined,
      },
    })),
  };
}

/**
 * `update`'s patch: only the scalar fields that actually changed, plus every
 * child set whole — the same split `TagsEditor` and `CustomFieldsEditor`
 * already draw between "a column" and "a set edited as a set", just applied
 * to the rest of the form in one save instead of two.
 */
function buildPatch(original: ContactWithDetails, state: FormState) {
  const payload = buildPayload(state);
  const patch: Partial<Contact> & {
    typeIds: string[];
    tags: string[];
    phones: ReturnType<typeof buildPayload>["phones"];
    emails: ReturnType<typeof buildPayload>["emails"];
    addresses: ReturnType<typeof buildPayload>["addresses"];
  } = {
    typeIds: payload.typeIds,
    tags: payload.tags,
    phones: payload.phones,
    emails: payload.emails,
    addresses: payload.addresses,
  };
  if (payload.displayName !== original.displayName) {
    patch.displayName = payload.displayName;
  }
  if (payload.companyName !== original.companyName) {
    patch.companyName = payload.companyName;
  }
  if (payload.description !== original.description) {
    patch.description = payload.description;
  }
  if (payload.gstin !== original.gstin) {
    patch.gstin = payload.gstin;
  }
  return patch;
}

function clientErrors(state: FormState): Record<string, string> | null {
  const fields: Record<string, string> = {};
  if (state.displayName.trim() === "") fields.displayName = "Required";

  const gstin = state.gstin.trim();
  if (gstin !== "" && !GSTIN_PATTERN.test(gstin.toUpperCase())) {
    fields.gstin = "Invalid GSTIN format";
  }
  if (state.phones.filter((phone) => phone.isPrimary).length > 1) {
    fields.phones = "Only one phone number can be primary";
  }
  if (state.emails.filter((email) => email.isPrimary).length > 1) {
    fields.emails = "Only one email address can be primary";
  }

  return Object.keys(fields).length > 0 ? fields : null;
}

function PhoneRows({
  phones,
  onChange,
  nextId,
}: {
  phones: PhoneDraft[];
  onChange: (next: PhoneDraft[]) => void;
  nextId: () => string;
}) {
  return (
    <div className="space-y-2">
      {phones.map((phone) => (
        <div
          key={phone.draftId}
          className="flex flex-wrap items-end gap-2 rounded-md border p-2"
        >
          <div className="min-w-24 flex-1 space-y-1">
            <Label className="text-xs">Label</Label>
            <Input
              value={phone.label}
              placeholder="e.g. Mobile"
              className="h-8"
              onChange={(event) => {
                onChange(
                  phones.map((p) =>
                    p.draftId === phone.draftId
                      ? { ...p, label: event.target.value }
                      : p,
                  ),
                );
              }}
            />
          </div>
          <div className="min-w-32 flex-1 space-y-1">
            <Label className="text-xs">Number</Label>
            <Input
              value={phone.number}
              placeholder="e.g. 98765 43210"
              className="h-8 tabular-nums"
              onChange={(event) => {
                onChange(
                  phones.map((p) =>
                    p.draftId === phone.draftId
                      ? { ...p, number: event.target.value }
                      : p,
                  ),
                );
              }}
            />
          </div>
          <div className="flex items-center gap-1.5 pb-1.5">
            <Checkbox
              id={`phone-primary-${phone.draftId}`}
              checked={phone.isPrimary}
              onCheckedChange={(checked) => {
                onChange(
                  phones.map((p) => ({
                    ...p,
                    isPrimary: p.draftId === phone.draftId && checked === true,
                  })),
                );
              }}
            />
            <Label
              htmlFor={`phone-primary-${phone.draftId}`}
              className="text-xs font-normal"
            >
              Primary
            </Label>
          </div>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Remove phone"
            className="mb-0.5"
            onClick={() => {
              onChange(phones.filter((p) => p.draftId !== phone.draftId));
            }}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          onChange([...phones, blankPhone(nextId())]);
        }}
      >
        <Plus />
        Add phone
      </Button>
    </div>
  );
}

function EmailRows({
  emails,
  onChange,
  nextId,
}: {
  emails: EmailDraft[];
  onChange: (next: EmailDraft[]) => void;
  nextId: () => string;
}) {
  return (
    <div className="space-y-2">
      {emails.map((email) => (
        <div
          key={email.draftId}
          className="flex flex-wrap items-end gap-2 rounded-md border p-2"
        >
          <div className="min-w-24 flex-1 space-y-1">
            <Label className="text-xs">Label</Label>
            <Input
              value={email.label}
              placeholder="e.g. Billing"
              className="h-8"
              onChange={(event) => {
                onChange(
                  emails.map((e) =>
                    e.draftId === email.draftId
                      ? { ...e, label: event.target.value }
                      : e,
                  ),
                );
              }}
            />
          </div>
          <div className="min-w-32 flex-1 space-y-1">
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={email.email}
              placeholder="name@example.com"
              className="h-8"
              onChange={(event) => {
                onChange(
                  emails.map((e) =>
                    e.draftId === email.draftId
                      ? { ...e, email: event.target.value }
                      : e,
                  ),
                );
              }}
            />
          </div>
          <div className="flex items-center gap-1.5 pb-1.5">
            <Checkbox
              id={`email-primary-${email.draftId}`}
              checked={email.isPrimary}
              onCheckedChange={(checked) => {
                onChange(
                  emails.map((e) => ({
                    ...e,
                    isPrimary: e.draftId === email.draftId && checked === true,
                  })),
                );
              }}
            />
            <Label
              htmlFor={`email-primary-${email.draftId}`}
              className="text-xs font-normal"
            >
              Primary
            </Label>
          </div>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Remove email"
            className="mb-0.5"
            onClick={() => {
              onChange(emails.filter((e) => e.draftId !== email.draftId));
            }}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          onChange([...emails, blankEmail(nextId())]);
        }}
      >
        <Plus />
        Add email
      </Button>
    </div>
  );
}

function AddressRows({
  addresses,
  onChange,
  nextId,
}: {
  addresses: AddressDraft[];
  onChange: (next: AddressDraft[]) => void;
  nextId: () => string;
}) {
  function update(draftId: string, patch: Partial<AddressDraft>) {
    onChange(
      addresses.map((address) =>
        address.draftId === draftId ? { ...address, ...patch } : address,
      ),
    );
  }

  return (
    <div className="space-y-2">
      {addresses.map((address) => (
        <div key={address.draftId} className="space-y-2 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <Input
              value={address.label}
              placeholder="Label (e.g. Warehouse)"
              className="h-8 flex-1"
              onChange={(event) => {
                update(address.draftId, { label: event.target.value });
              }}
            />
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label="Remove address"
              onClick={() => {
                onChange(
                  addresses.filter((a) => a.draftId !== address.draftId),
                );
              }}
            >
              <Trash2 />
            </Button>
          </div>
          <Input
            value={address.line1}
            placeholder="Address line 1"
            className="h-8"
            onChange={(event) => {
              update(address.draftId, { line1: event.target.value });
            }}
          />
          <Input
            value={address.line2}
            placeholder="Address line 2"
            className="h-8"
            onChange={(event) => {
              update(address.draftId, { line2: event.target.value });
            }}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={address.city}
              placeholder="City"
              className="h-8"
              onChange={(event) => {
                update(address.draftId, { city: event.target.value });
              }}
            />
            <Input
              value={address.province}
              placeholder="State"
              className="h-8"
              onChange={(event) => {
                update(address.draftId, { province: event.target.value });
              }}
            />
            <Input
              value={address.zip}
              placeholder="Postal code"
              className="h-8 tabular-nums"
              onChange={(event) => {
                update(address.draftId, { zip: event.target.value });
              }}
            />
            <Input
              value={address.country}
              placeholder="Country"
              className="h-8"
              onChange={(event) => {
                update(address.draftId, { country: event.target.value });
              }}
            />
          </div>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          onChange([...addresses, blankAddress(nextId())]);
        }}
      >
        <Plus />
        Add address
      </Button>
    </div>
  );
}

/**
 * "New contact" and "Edit details" (#387) — a `Sheet` rather than a page:
 * `RecordDrawer` already gives every other write panel in this application
 * the focus trap, the Escape handler and the full-screen-on-a-phone width,
 * and a contact form asks for nothing a second implementation would do
 * differently.
 *
 * Fully self-resetting rather than controlled from outside: `reset()` runs
 * whenever the sheet closes — Cancel, Escape, the overlay, or a successful
 * save — so reopening it always starts from `contact`'s current state (edit)
 * or blank plus `initialTypeIds` (create), never from whatever was left
 * half-typed the previous time.
 */
export function ContactForm(props: ContactFormProps) {
  const { open, onOpenChange, onSaved } = props;
  const data = useDataSource();
  const queryClient = useQueryClient();

  const typesQuery = useQuery({
    queryKey: ["contacts", "types"],
    queryFn: () => data.contacts.listTypes(),
  });

  const [state, setState] = useState<FormState>(() => initialState(props));
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [counter, setCounter] = useState(0);

  function nextId(): string {
    const id = `draft-${String(counter)}`;
    setCounter((n) => n + 1);
    return id;
  }

  function reset() {
    setState(initialState(props));
    setRejection(null);
  }

  const mutation = useMutation({
    mutationFn: () =>
      props.mode === "create"
        ? data.contacts.create(buildPayload(state))
        : data.contacts.update(
            props.contact.id,
            buildPatch(props.contact, state),
          ),
    onSuccess: (result) => {
      if (!result.ok) {
        setRejection(result.rejection);
        return;
      }
      onOpenChange(false);
      // Write-through: the row this opens next (or redraws in place) comes
      // from `result.data`, not from `state` — CLAUDE.md's "the local row is
      // written from the mutation's response rather than from the submitted
      // form" applies to a create just as much as an edit.
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      onSaved?.(result.data);
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const fields = clientErrors(state);
    if (fields) {
      setRejection({
        kind: "invalid",
        message: "Check the highlighted fields.",
        fields,
      });
      return;
    }
    setRejection(null);
    mutation.mutate();
  }

  const fieldError = (field: string): string | undefined =>
    rejection?.kind === "invalid" ? rejection.fields?.[field] : undefined;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <SheetContent
        title={props.mode === "create" ? "New contact" : "Edit contact"}
        description={
          props.mode === "create"
            ? "Add a person or business to the address book."
            : `Update ${props.contact.displayName}'s details.`
        }
        actions={<FeedbackButton />}
        className="w-full sm:w-[640px] sm:max-w-[92vw]"
      >
        <form className="flex h-full flex-col" onSubmit={handleSubmit}>
          <div className="flex-1 space-y-4 overflow-auto p-4 sm:p-6">
            <div className="space-y-1.5">
              <Label htmlFor="contact-display-name">Display name</Label>
              <Input
                id="contact-display-name"
                value={state.displayName}
                onChange={(event) => {
                  setState((prev) => ({
                    ...prev,
                    displayName: event.target.value,
                  }));
                }}
                required
              />
              {fieldError("displayName") ? (
                <p className="text-destructive text-xs">
                  {fieldError("displayName")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact-company-name">Company name</Label>
              <Input
                id="contact-company-name"
                value={state.companyName}
                onChange={(event) => {
                  setState((prev) => ({
                    ...prev,
                    companyName: event.target.value,
                  }));
                }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact-description">Description</Label>
              <Textarea
                id="contact-description"
                value={state.description}
                onChange={(event) => {
                  setState((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }));
                }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact-gstin">GSTIN</Label>
              <Input
                id="contact-gstin"
                value={state.gstin}
                placeholder="e.g. 08AABCR1234M1Z5"
                maxLength={15}
                className="tabular-nums"
                onChange={(event) => {
                  setState((prev) => ({
                    ...prev,
                    gstin: event.target.value.toUpperCase(),
                  }));
                }}
              />
              {fieldError("gstin") ? (
                <p className="text-destructive text-xs">
                  {fieldError("gstin")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label>Contact types</Label>
              <div className="space-y-2 rounded-md border p-3">
                {typesQuery.isPending ? (
                  <p className="text-muted-foreground text-sm">Loading…</p>
                ) : typesQuery.isError ? (
                  <p className="text-destructive text-sm">
                    Couldn't load contact types.
                  </p>
                ) : typesQuery.data.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No contact types exist yet.
                  </p>
                ) : (
                  typesQuery.data.map((type) => (
                    <div key={type.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`contact-type-${type.id}`}
                        checked={state.typeIds.includes(type.id)}
                        onCheckedChange={(checked) => {
                          setState((prev) => ({
                            ...prev,
                            typeIds:
                              checked === true
                                ? [...prev.typeIds, type.id]
                                : prev.typeIds.filter((id) => id !== type.id),
                          }));
                        }}
                      />
                      <Label
                        htmlFor={`contact-type-${type.id}`}
                        className="text-sm font-normal"
                      >
                        {type.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Phone numbers</Label>
              <PhoneRows
                phones={state.phones}
                nextId={nextId}
                onChange={(phones) => {
                  setState((prev) => ({ ...prev, phones }));
                }}
              />
              {fieldError("phones") ? (
                <p className="text-destructive text-xs">
                  {fieldError("phones")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label>Email addresses</Label>
              <EmailRows
                emails={state.emails}
                nextId={nextId}
                onChange={(emails) => {
                  setState((prev) => ({ ...prev, emails }));
                }}
              />
              {fieldError("emails") ? (
                <p className="text-destructive text-xs">
                  {fieldError("emails")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label>Addresses</Label>
              <AddressRows
                addresses={state.addresses}
                nextId={nextId}
                onChange={(addresses) => {
                  setState((prev) => ({ ...prev, addresses }));
                }}
              />
              {fieldError("addresses") ? (
                <p className="text-destructive text-xs">
                  {fieldError("addresses")}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label>Tags</Label>
              <TokenInput
                values={state.tags}
                onChange={(tags) => {
                  setState((prev) => ({ ...prev, tags }));
                }}
                placeholder="Type a tag and press Enter"
              />
            </div>

            {rejection && rejection.kind !== "invalid" ? (
              <RejectionNotice rejection={rejection} />
            ) : null}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t p-4 sm:px-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || state.displayName.trim() === ""}
            >
              {mutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              {props.mode === "create" ? "Create contact" : "Save changes"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
